import { FCC } from "@/types/common";
import { CaseTitle } from "@/features/case/components/CaseTitle";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useJobInfo } from "@/api/services/main/job";
import { CaseStep } from "@/features/case/components/CaseStep";
import { CaseBasicInfo } from "@/features/case/components/CaseBasicInfo/CaseBasicInfo";
import { useForm } from "react-hook-form";
import { TCaseBasicInfo } from "@/features/case/types/case";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCaseSchema } from "@/features/case/schemas/caseSchema";
import { useCaseInfo } from "@/api/services/main/case";
import { ChangeEvent, useEffect, useState } from "react";
import { CaseEditMatches } from "@/features/case/components/CaseEdit/CaseEditMatches";
import { START_PAGE } from "@/constants/pagination";
import { usePagination } from "@/hooks/usePagination";
import { TGetCaseRequestDto } from "@/api/services/main/case/dtos/get-case.dto";
import { Stack, Typography } from "@mui/material";
import { Textarea } from "@/components/elements/Textarea";
import { InnerBox } from "@/components/elements/InnerBox";
import { Button } from "@/components/elements/Button";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { AppRoutes } from "@/routes/config";
import { useValidateId } from "@/hooks/useValidateId";
import { Form } from "@/components/elements/Form";

export const CaseEditPage: FCC = () => {
  const { jobId, caseId } = useParams();
  const navigate = useNavigate();
  const pagReq = usePagination();
  const jobQueryResult = useJobInfo(Number(jobId));
  const { data: jobInfoResponse } = jobQueryResult;
  const jobInfo = jobInfoResponse?.data ?? {};
  const [searchParams, _setSearchParams] = useSearchParams();
  const [memo, setMemo] = useState<string>("");
  const query: TGetCaseRequestDto = {
    page: pagReq.page || START_PAGE,
    perPage: 10,
  };

  if (searchParams.get("sorts")) {
    query.sorts = searchParams.get("sorts") ? [searchParams.get("sorts") as `${string}|${string}`] : [];
  }
  const caseQueryResult = useCaseInfo(Number(caseId), query);
  const { data: caseInfoResponse } = caseQueryResult;
  const caseInfo = caseInfoResponse?.data;

  useValidateId("jobId", AppRoutes.caseList, jobQueryResult);
  useValidateId("caseId", AppRoutes.caseList, caseQueryResult);
  const activeStep = 2;
  const { caseSchema } = useCaseSchema();
  const methods = useForm<TCaseBasicInfo>({
    defaultValues: {},
    resolver: yupResolver<TCaseBasicInfo>(caseSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  useEffect(() => {
    if (caseInfo) {
      methods.reset(caseInfo);
    }
  }, [caseInfo]);
  const onChangeMemo = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMemo(e.target.value);
  };

  const onSubmit = () => {};
  return (
    <Form methods={methods} onSubmit={onSubmit} schema={caseSchema}>
      <CaseTitle jobInfo={jobInfo} />
      <CaseStep activeStep={activeStep} />
      <CaseBasicInfo jobInfo={jobInfo} />
      <CaseEditMatches caseMatches={caseInfo?.caseMatches ?? []} />
      <InnerBox mt={3} mb={3} showDivider={false} title={<Typography variant="head20Bold">案件メモ</Typography>}>
        <Textarea value={memo} onChange={onChangeMemo} />
      </InnerBox>
      <FooterContainer>
        <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" gap={2}>
            <Button variant="ghost" onClick={() => navigate(AppRoutes.caseList)}>
              案件一覧に戻る
            </Button>
          </Stack>
          <Stack direction="row" gap={3}>
            <Button type="submit" variant="outline" size="sm" onClick={onSubmit}>
              新しい案件に分割
            </Button>
            <Button type="submit" variant="primary" size="sm" onClick={onSubmit}>
              ステータスの更新
            </Button>
          </Stack>
        </Stack>
      </FooterContainer>
    </Form>
  );
};
